export const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(amount);
};

export const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    });
};

export const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};

export const formatNumber = (number, decimalPlaces = 2) => {
    return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: decimalPlaces,
        maximumFractionDigits: decimalPlaces
    }).format(number);
};

export const formatPercentage = (value) => {
    return `${formatNumber(value, 1)}%`;
};

// ---------------------------------------------------------------------------
// International events: currency + timezone helpers
// ---------------------------------------------------------------------------

const CURRENCY_SYMBOLS = {
  KES: 'KSh', USD: '$', EUR: '€', GBP: '£', NGN: '₦', ZAR: 'R',
  UGX: 'USh', TZS: 'TSh', RWF: 'FRw', GHS: 'GH₵', INR: '₹',
  AED: 'د.إ', CAD: 'C$', AUD: 'A$',
};

export const SUPPORTED_CURRENCIES = [
  { code: 'KES', label: 'KES — Kenyan Shilling' },
  { code: 'USD', label: 'USD — US Dollar' },
  { code: 'EUR', label: 'EUR — Euro' },
  { code: 'GBP', label: 'GBP — British Pound' },
  { code: 'NGN', label: 'NGN — Nigerian Naira' },
  { code: 'ZAR', label: 'ZAR — South African Rand' },
  { code: 'UGX', label: 'UGX — Ugandan Shilling' },
  { code: 'TZS', label: 'TZS — Tanzanian Shilling' },
  { code: 'RWF', label: 'RWF — Rwandan Franc' },
  { code: 'GHS', label: 'GHS — Ghanaian Cedi' },
  { code: 'INR', label: 'INR — Indian Rupee' },
  { code: 'AED', label: 'AED — UAE Dirham' },
  { code: 'CAD', label: 'CAD — Canadian Dollar' },
  { code: 'AUD', label: 'AUD — Australian Dollar' },
];

export function currencySymbol(currency = 'KES') {
  return CURRENCY_SYMBOLS[currency] || currency || '';
}

/** Format a money amount in the given currency (Intl, with graceful fallback). */
export function formatMoney(amount, currency = 'KES') {
  const value = Number(amount || 0);
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency || 'KES',
      maximumFractionDigits: value % 1 === 0 ? 0 : 2,
    }).format(value);
  } catch (e) {
    return `${currencySymbol(currency)} ${value.toLocaleString()}`;
  }
}

/** Render an event's date+time in its timezone. */
export function formatEventDateTime(date, time, timeZone) {
  if (!date) return { full: '', dateStr: '', timeStr: '', iso: '' };
  const iso = `${date}T${(time || '00:00').slice(0, 8)}`;
  const dt = new Date(iso);
  if (isNaN(dt.getTime())) return { full: `${date} ${time || ''}`.trim(), dateStr: date, timeStr: time || '', iso };

  const opts = { weekday: 'short', year: 'numeric', month: 'long', day: 'numeric' };
  const timeOpts = { hour: '2-digit', minute: '2-digit' };
  if (timeZone) { opts.timeZone = timeZone; timeOpts.timeZone = timeZone; timeOpts.timeZoneName = 'short'; }

  try {
    const dateStr = new Intl.DateTimeFormat(undefined, opts).format(dt);
    const timeStr = new Intl.DateTimeFormat(undefined, timeOpts).format(dt);
    return { full: `${dateStr} · ${timeStr}`, dateStr, timeStr, iso };
  } catch (e) {
    return { full: `${date} ${time || ''}`.trim(), dateStr: date, timeStr: time || '', iso };
  }
}

export const COMMON_TIMEZONES = [
  'Africa/Nairobi', 'Africa/Lagos', 'Africa/Johannesburg', 'Africa/Cairo', 'Africa/Accra',
  'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Europe/Madrid',
  'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles', 'America/Toronto',
  'Asia/Dubai', 'Asia/Kolkata', 'Asia/Singapore', 'Asia/Tokyo', 'Asia/Shanghai',
  'Australia/Sydney', 'UTC',
];

export function guessTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Africa/Nairobi';
  } catch (e) {
    return 'Africa/Nairobi';
  }
}

/** Build a downloadable .ics calendar file for an event and trigger download. */
export function downloadEventICS(event) {
  if (!event?.date) return;
  const start = new Date(`${event.date}T${(event.time || '00:00').slice(0, 8)}`);
  const end = new Date(start.getTime() + 2 * 60 * 60 * 1000); // default 2h
  const fmt = (d) => d.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
  const esc = (s) => String(s || '').replace(/([,;\\])/g, '\\$1').replace(/\n/g, '\\n');
  const lines = [
    'BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//EventSawa//EN', 'BEGIN:VEVENT',
    `UID:event-${event.id || event.slug}@eventsawa`,
    `DTSTAMP:${fmt(new Date())}`,
    `DTSTART:${fmt(start)}`,
    `DTEND:${fmt(end)}`,
    `SUMMARY:${esc(event.title)}`,
    `DESCRIPTION:${esc(event.description)}`,
    `LOCATION:${esc(event.location)}`,
    'END:VEVENT', 'END:VCALENDAR',
  ];
  const blob = new Blob([lines.join('\r\n')], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${(event.slug || 'event')}.ics`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
